import type { FigmaFilesAPIData } from "@/src/types/index.ts";
import type { ZeytalCLIPureConfig } from "@zeytal/kit";
import type { ZeytalApi } from "@zeytal/types";
import type { OptionValues } from "commander";

import * as path from "node:path";

import { wait } from "@endgame/utils/wait";
import {
  downloadAsset,
  getNestedNodesByFileId,
  optimizeAsset,
  sortNodesByExportSettings,
} from "@zeytal/assets";
import { getFigmaImagesEndpoint } from "@zeytal/figma";
import { zeytalDefaultConfig } from "@zeytal/kit";
import { projectTokenHeaderName } from "@zeytal/kit/api";
import { cyan, green, magenta } from "colorette";
import { Command } from "commander";
import fetch from "node-fetch";

import { fetchFilesData, fetchFilesVersions } from "@/src/lib/figma.ts";
import { errorMessages } from "@/src/utils/errors.ts";
import { refreshFolder, writeFile } from "@/src/utils/file-system.ts";
import { getConfig } from "@/src/utils/get-config.ts";
import { getCWD } from "@/src/utils/get-cwd.ts";
import { getZeytalApiBaseUrl } from "@/src/utils/get-zeytal-api-base-url.ts";
import { icons } from "@/src/utils/icons.ts";
import { logger } from "@/src/utils/logger.ts";

const assetsActionCore = async (
  commandOptions: OptionValues,
  zeytalConfig: ZeytalCLIPureConfig,
) => {
  logger.log(`${magenta(icons.leftHalfCircle)} Assets fetching started...`);

  logger.spinner().start("Assessing your configuration parameters...");

  const zeytalAPIBaseUrl = getZeytalApiBaseUrl();

  logger.debug("Getting user’s zeytal project data and figma files list.");

  const figmaAssetsAPIRes = await fetch(`${zeytalAPIBaseUrl}/api/assets`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      [projectTokenHeaderName]: zeytalConfig.projectToken ?? "",
    },
  }).catch(() => {
    logger.spinner().fail();
    throw errorMessages.zeytalApiError500();
  });

  const figmaAssetsAPIData =
    (await figmaAssetsAPIRes.json()) as ZeytalApi.ApiResponse<FigmaFilesAPIData>;

  if (!figmaAssetsAPIData || !figmaAssetsAPIData.success) {
    logger.spinner().fail();
    throw figmaAssetsAPIData.message;
  }

  if (!figmaAssetsAPIData.data || figmaAssetsAPIData.data === null) {
    logger.spinner().fail();
    throw errorMessages.zeytalApiNoData();
  }

  logger.spinner().succeed();

  logger
    .spinner()
    .start(
      `Searching for the latest ${zeytalConfig.assets.figmaFilesVersion} version of your figma files...`,
    );

  if (!figmaAssetsAPIData.data.projectData.figmaApiKey) {
    logger.spinner().fail();
    throw errorMessages.noFigmaApiKey({
      projectName: figmaAssetsAPIData.data.projectData.name,
    });
  }

  // Get the right files versions
  const filesWithVersion =
    (await fetchFilesVersions(
      figmaAssetsAPIData.data.figmaFiles,
      figmaAssetsAPIData.data.projectData.figmaApiKey,
      zeytalConfig,
      "assets",
    )) ?? [];

  logger.spinner().succeed();

  logger.spinner().start("Figma files fetching started...");

  const currentPagesBatchSize = zeytalConfig.assets.figmaFilesPagesBatchSize;
  const cleanPagesBatchSize =
    !currentPagesBatchSize ||
    typeof currentPagesBatchSize !== "number" ||
    currentPagesBatchSize <= 1
      ? zeytalDefaultConfig.assets.figmaFilesPagesBatchSize
      : currentPagesBatchSize;

  logger.spinner().succeed();

  // Get the pages in the Figma files that are not empty
  const figmaFilesData = await fetchFilesData(
    cleanPagesBatchSize,
    filesWithVersion,
    figmaAssetsAPIData.data.projectData.figmaApiKey,
  );

  logger.spinner().start("Processing your figma files data...");

  logger.debug("Nesting Figma files nodes.");
  const nestedNodes = getNestedNodesByFileId(figmaFilesData);

  logger.debug("Filtering and sorting Figma files nodes.");
  const sortedNodes = sortNodesByExportSettings(nestedNodes);

  logger.spinner().succeed();

  logger.spinner().succeed("Requesting your figma assets URLs...");

  const filesAssetsUrls: [string, string][] = [];

  let urlsBatchIndex = 0;
  for (const [fileId, settingsForAssetsIds] of sortedNodes) {
    urlsBatchIndex += 1;

    logger.resetSpinner();
    logger
      .spinner()
      .start(
        `Fetching assets URLs batch ${urlsBatchIndex}/${sortedNodes.length}`,
      );

    for (const [settingParams, rawAssetsData] of Object.entries(
      settingsForAssetsIds,
    )) {
      const ids = rawAssetsData.map(({ id }) => id).join(",");
      const queryParams = settingParams + `&ids=${ids}`;

      logger.debug("Fetching data from Figma assets endpoints.");
      const { images: assetsUrls } = await getFigmaImagesEndpoint(
        figmaAssetsAPIData.data.projectData.figmaApiKey,
        fileId,
        queryParams,
      );

      if (filesAssetsUrls.length > 0) {
        // delay between requests
        logger.debug("Delaying requests to avoid rejections from Figma’s API.");
        await wait(50);
      }

      // Process each asset and add to results
      for (const { id, name } of rawAssetsData) {
        if (!assetsUrls[id]) continue;
        filesAssetsUrls.push([name, assetsUrls[id]]);
      }
    }

    logger.spinner().succeed();
  }

  const assetsFilesTuples: [string, string][] = [];
  for (const [assetName, assetUrl] of filesAssetsUrls) {
    const fileName = assetName.slice(assetName.indexOf("/") + 1);

    logger.log(
      `${green(icons.checkmark)} Downloading and optimizing ${cyan(fileName)}`,
    );

    logger.debug("Downloading asset.");
    const assetData = await downloadAsset(assetUrl);

    logger.debug(
      "Delaying next request to avoid rejections from Figma’s servers.",
    );
    // Avoiding Figma AWS server limits restrictions.
    await wait(150);

    if (!assetData) {
      logger.warn(`${icons.warning} No asset data found for ${cyan(fileName)}`);
    } else {
      logger.debug("Optimizing asset.");

      const optimizedAssetData = optimizeAsset(
        zeytalConfig,
        assetName,
        assetData,
      );

      assetsFilesTuples.push([assetName, optimizedAssetData]);
    }
  }

  logger.spinner().succeed("All assets downloaded and optimized.");

  logger
    .spinner()
    .succeed(
      `Writing assets files inside ${cyan(zeytalConfig.assets.directoryPath)}...`,
    );

  // ⚡ Avoid computing and creating variables at each step of the loop
  const refreshedFolders: string[] = [];
  const assetsFilesLength = assetsFilesTuples.length;
  let writingLoopIndex = 0;

  // Writing assets files
  for (
    writingLoopIndex;
    writingLoopIndex < assetsFilesLength;
    writingLoopIndex += 1
  ) {
    const [filePath, fileContent] = assetsFilesTuples[writingLoopIndex]!;
    const writePath = path.join(
      commandOptions.cwd,
      zeytalConfig.assets.directoryPath,
      filePath,
    );

    const [assetType, fileName] = filePath.split("/");

    logger.resetSpinner();
    logger.spinner().start(`Writing ${cyan(fileName ?? "")}...`);

    if (assetType) {
      logger.debug("Refreshing assets directory.");

      const assetTypeFolderPath = path.join(
        commandOptions.cwd,
        zeytalConfig.assets.directoryPath,
        assetType,
      );

      // Refresh assets type folder only once
      if (!refreshedFolders.includes(assetTypeFolderPath)) {
        // debug(`Step: Assets of type "${assetType}" folder created.`);
        await refreshFolder(assetTypeFolderPath);
        refreshedFolders.push(assetTypeFolderPath);
      }
    }

    logger.debug("Writing asset.");

    writeFile(writePath, Buffer.from(fileContent, "base64"));

    logger.debug("Asset written.");
    logger.spinner().succeed(`${cyan(fileName ?? "")} written.`);
  }

  logger
    .spinner()
    .succeed(
      `All assets files written to ${cyan(zeytalConfig.assets.directoryPath)}`,
    );
};

export const assetsCmd = new Command()
  .name("assets")
  .description("extract and optimize SVGs from your Figma files")
  .action(async (_, command) => {
    logger.break();

    const optsWithGlobals = command.optsWithGlobals();
    logger.setDebugModeTo(optsWithGlobals.debug);

    const options = { ...optsWithGlobals, cwd: getCWD(optsWithGlobals.cwd) };

    logger.debug("Getting user’s zeytal CLI config.");
    const zeytalConfig = await getConfig(options.cwd);

    try {
      await assetsActionCore(options, zeytalConfig);
    } catch (errorMessage) {
      logger.error(errorMessage);
      logger.break();
      process.exit(1);
    }
  });
